import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import Contact, { loader as ContactLoader } from "../pages/Index/Contact";
import DiscountLodging, {
    loader as DiscountLodgingLoader,
} from "../pages/Index/DiscountLodging";
import Field, { loader as FieldLoader } from "../pages/Index/Field";
import Fields, { loader as FieldsLoader } from "../pages/Index/Fields";
import HomePage, { loader as HomePageLoader } from "../pages/Index/HomePage";
import Login from "../pages/Index/Login";
import LostPassword, {
    action as LostPasswordAction,
} from "../pages/Index/LostPassword";
import PhotoUpload from "../pages/Index/PhotoUpload";
import Profile, { loader as ProfileLoader } from "../pages/Index/Profile";
import Register, { loader as RegisterLoader } from "../pages/Index/Register";
import RegisterTeam, {
    loader as RegisterTeamLoader,
} from "../pages/Index/RegisterTeam";
import ResetPassword, {
    loader as ResetPasswordLoader,
} from "../pages/Index/ResetPassword";
import Rules, { loader as RulesLoader } from "../pages/Index/Rules";
import Testimonials from "../pages/Index/Testimonials";
import ThankYou, { loader as ThankYouLoader } from "../pages/Index/ThankYou";
import Vetting, { loader as VettingLoader } from "../pages/Index/Vetting";
import Waiver, { loader as WaiverLoader } from "../pages/Index/Waiver";
import { IUserProfileData } from "../pages/SiteManagement/Users/types/users";
import { IAPIResponse, IField } from "../types/api";

export default (
    <Route>
        <Route
            index
            element={<HomePage />}
            loader={HomePageLoader}
            handle={{ noHeader: true }}
        />
        <Route
            path="contact-us"
            element={<Contact />}
            loader={ContactLoader}
            handle={{
                crumb: () => (
                    <Link to="/contact-us">
                        Contact Wisconsin Baseball Tournaments
                    </Link>
                ),
                header: () => "Contact Wisconsin Baseball Tournaments",
            }}
        />
        <Route
            path="photo-upload"
            element={<PhotoUpload />}
            handle={{
                crumb: () => <Link to="/photo-upload">Photo Upload</Link>,
                header: () => "Photo Upload",
            }}
        />
        <Route
            path="testimonials"
            element={<Testimonials />}
            handle={{
                crumb: () => (
                    <Link to="/testimonials">What Others Have to Say...</Link>
                ),
                header: () =>
                    "What others have to say about Wisconsin Baseball Tournaments",
            }}
        />
        <Route
            path="rules"
            handle={{
                crumb: () => <Link to="/rules">Rules</Link>,
                header: () => "Tournament Rules",
            }}
            element={<Rules />}
            loader={params =>
                RulesLoader({
                    ...params,
                    content: "wibb_rules",
                })
            }
        />
        <Route
            path="fields"
            handle={{
                crumb: () => <Link to="/fields">League Fields</Link>,
            }}
        >
            <Route
                index
                element={<Fields />}
                loader={FieldsLoader}
                handle={{
                    header: () => "Tournament Fields",
                }}
            />
            <Route
                path=":id"
                element={<Field />}
                loader={FieldLoader}
                handle={{
                    crumb: ({
                        field: { field: data },
                    }: {
                        field: IAPIResponse & { field: IField };
                    }) => <Link to={`/fields/${data.id}`}>{data.name}</Link>,
                    header: ({
                        field: { field },
                    }: {
                        field: IAPIResponse & { field: IField };
                    }) => field.name,
                }}
            />
        </Route>
        <Route
            path="login"
            element={<Login />}
            handle={{
                crumb: () => <Link to="/login">Login</Link>,
                header: () => "Login",
            }}
        />
        <Route
            path="lost-password"
            element={<LostPassword />}
            action={LostPasswordAction}
            handle={{
                crumb: () => <Link to="/lost-password">Lost Password</Link>,
                header: () => "Lost Password",
            }}
        />
        <Route
            path="reset-password"
            element={<ResetPassword />}
            loader={ResetPasswordLoader}
            handle={{
                crumb: () => <Link to="/reset-password">Reset Password</Link>,
                header: () => "Reset Password",
            }}
        />
        <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.all} />}>
            <Route
                path="register/tournament"
                element={<RegisterTeam />}
                loader={RegisterTeamLoader}
                handle={{
                    crumb: () => (
                        <Link to="/register/tournament">
                            Tournament Registration
                        </Link>
                    ),
                    header: () => "Tournament Registration",
                }}
            />
        </Route>
        <Route
            path="register/:userType"
            element={<Register />}
            loader={RegisterLoader}
            handle={{
                crumb: ({ userType }: { userType: string }) => {
                    let crumbDisplay;

                    switch (userType) {
                        case "coach":
                            crumbDisplay = "Coach";
                            break;
                    }

                    return (
                        <Link to={`/register/${crumbDisplay}`}>
                            Register {crumbDisplay}
                        </Link>
                    );
                },
                header: ({ userType }: { userType: string }) => {
                    let headerDisplay;
                    switch (userType) {
                        case "coach":
                            headerDisplay = "Tournament Registration";
                            break;
                    }
                    return headerDisplay;
                },
            }}
        />
        <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.all} />}>
            <Route
                path="profile"
                element={<Profile />}
                loader={ProfileLoader}
                handle={{
                    crumb: () => <Link to="profile">My Profile</Link>,
                    header: () => "My Profile",
                }}
            />
        </Route>
        <Route
            element={
                <RequireAuth
                    allowedUserLevels={AUTH_LEVELS.tourneymanagement}
                />
            }
        >
            <Route
                path="profile/:userId"
                element={<Profile />}
                loader={ProfileLoader}
                handle={{
                    crumb: (data: IUserProfileData) => (
                        <Link to={`profile/${data.id}`}>Edit User Profile</Link>
                    ),
                }}
            />
        </Route>
        <Route
            path="thankyou"
            element={<ThankYou />}
            loader={ThankYouLoader}
            handle={{
                crumb: () => <Link to={`thankyou`}>Checkout Complete</Link>,
                header: () => "Checkout Complete",
            }}
        />
        <Route
            path="waiver/:playerId"
            element={<Waiver />}
            loader={WaiverLoader}
            handle={{
                crumb: () => "Player Waiver Acceptance",
                header: () => "Player Waiver Acceptance",
            }}
        />
        <Route
            path="discount-lodging"
            element={<DiscountLodging />}
            loader={DiscountLodgingLoader}
            handle={{
                crumb: () => "Discount Lodging Options",
                header: () => "Discount Lodging Options",
            }}
        />
        <Route
            path="vetting"
            element={<Vetting />}
            loader={VettingLoader}
            handle={{
                crumb: () => "Vetting Process",
                header: () => "Why A Thorough Vetting Process Is Important",
            }}
        />
        <Route path="*" element={<NotFound parent="index" />} />
    </Route>
);
